import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ToastrService } from 'ngx-toastr';
import { HeaderService } from '../../../../services/header/header.service';
import { ExportService } from '../../shared/export.service';
import { LogicielCaisseReasortTransfertService } from '../../../../services/logiciel-caisse/logiciel-caisse-reasort-transfert.service';

export type ReassortTab = 'en-cours' | 'clos';

@Component({
  selector: 'app-reassort',
  templateUrl: './reassort.component.html',
  styleUrls: ['./reassort.component.scss'],
})
export class ReassortComponent implements OnInit, OnDestroy {
  currentTab: ReassortTab = 'en-cours';
  counts = { en_cours: 0, clos: 0 };
  lancerBusy = false;

  private navSub?: Subscription;
  private countsSub?: Subscription;

  readonly tabs: { path: ReassortTab; label: string; apiKey: 'en_cours' | 'clos' }[] = [
    { path: 'en-cours', label: 'En cours', apiKey: 'en_cours' },
    { path: 'clos', label: 'Clos', apiKey: 'clos' },
  ];

  constructor(
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly headerService: HeaderService,
    private readonly reasortService: LogicielCaisseReasortTransfertService,
    private readonly exportService: ExportService,
    private readonly toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.applyHeader();
    this.syncTabFromUrl();
    this.loadCounts();
    this.navSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        this.syncTabFromUrl();
        this.loadCounts();
      });
  }

  ngOnDestroy(): void {
    this.navSub?.unsubscribe();
    this.countsSub?.unsubscribe();
  }

  filterByTab(tab: ReassortTab): void {
    this.currentTab = tab;
    void this.router.navigate(['/achat/reassort', tab]);
  }

  getCount(apiKey: 'en_cours' | 'clos'): number {
    return this.counts[apiKey] ?? 0;
  }

  lancerReassort(): void {
    if (this.lancerBusy) {
      return;
    }
    this.lancerBusy = true;
    this.applyHeader();
    this.reasortService.lancerReassortTest().subscribe({
      next: (res) => {
        this.lancerBusy = false;
        this.applyHeader();
        const transfers = res.summary?.transfers ?? res.created?.length ?? 0;
        const lines = res.summary?.lines ?? 0;
        if (transfers > 0) {
          this.toastr.success(
            res.summary?.message ||
              `Réassort lancé : ${transfers} document(s) créé(s), ${lines} ligne(s).`,
            'Réassort'
          );
        } else {
          this.toastr.info(
            res.summary?.message ||
              'Aucun transfert généré — vérifier min/max (reasort_config), priorités et stocks sources.',
            'Réassort'
          );
        }
        if (res.warnings?.length) {
          this.toastr.warning(res.warnings.slice(0, 3).join(' ; '), 'Avertissements');
        }
        this.loadCounts();
        this.reasortService.notifyListRefresh();
      },
      error: (err: Error) => {
        this.lancerBusy = false;
        this.applyHeader();
        this.toastr.error(err?.message || 'Échec du lancement réassort', 'Erreur');
      },
    });
  }

  private syncTabFromUrl(): void {
    const path = this.route.firstChild?.snapshot?.url?.[0]?.path;
    this.currentTab = path === 'clos' ? 'clos' : 'en-cours';
  }

  private loadCounts(): void {
    this.countsSub?.unsubscribe();
    this.countsSub = this.reasortService.getCounts().subscribe({
      next: (c) => {
        this.counts = c;
      },
      error: () => {
        this.counts = { en_cours: 0, clos: 0 };
      },
    });
  }

  private applyHeader(): void {
    this.headerService.setConfig({
      title: 'Réassort',
      buttons: [
        {
          label: 'Exporter',
          type: 'dropdown',
          dropdownItems: [
            {
              label: 'PDF',
              action: () => this.exportService.triggerPDF(),
              icon: '../../../assets/images/logo/pdf_logo.png',
            },
            {
              label: 'CSV',
              action: () => this.toastr.info('Export CSV à venir', 'Export'),
              icon: '../../../assets/images/logo/csv_logo.png',
            },
          ],
        },
        {
          label: this.lancerBusy ? 'Lancement…' : 'Lancer réassort',
          type: 'primary',
          action: () => this.lancerReassort(),
        },
      ],
    });
  }
}
